// Marketplace Administration Page
// Feature: 006-marketplace-management

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, ShoppingBag } from 'lucide-react';
import { useMarketplaceAdministration } from '@/hooks/useMarketplaceAdministration';
import { buildImageUrl } from '@/services/api/images';
import { MarketplaceFilters } from '@/components/admin/MarketplaceFilters';
import { MarketplaceDetail } from '@/components/admin/MarketplaceDetail';
import { MarketplaceStatusBadge } from '@/components/admin/MarketplaceStatusBadge';

export default function MarketplaceAdministrationPage() {
  const { t } = useTranslation('marketplaceAdmin');
  const { itemId } = useParams<{ itemId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = location.pathname.endsWith('/edit');
  
  const {
    items,
    selectedItem,
    categories,
    searchTerm,
    statusFilter,
    categoryFilter,
    currentPage,
    totalPages,
    totalCount,
    isLoading,
    isLoadingDetails,
    isEditing,
    setSearchTerm,
    setStatusFilter,
    setCategoryFilter,
    selectItem,
    clearSelection,
    deleteItem,
    restoreItem,
    expireItem,
    reactivateItem,
    startEditing,
    cancelEditing,
    saveItemChanges,
    previousPage,
    nextPage,
  } = useMarketplaceAdministration();

  // Determine if we're in detail view mode (URL has itemId)
  const isDetailView = !!itemId;

  // Load item when URL has itemId
  useEffect(() => {
    if (itemId) {
      selectItem(itemId);
      // If URL ends with /edit, start editing mode
      if (isEditMode) {
        startEditing();
      }
    } else {
      clearSelection();
    }
  }, [itemId, isEditMode, selectItem, clearSelection, startEditing]);

  // Navigate to item detail
  const handleSelectItem = (id: string) => {
    navigate(`/admin/marketplace/${id}`);
  };

  // Navigate back to list
  const handleBackToList = () => {
    navigate('/admin/marketplace');
  };

  // Navigate to edit mode
  const handleEdit = () => {
    if (selectedItem) {
      navigate(`/admin/marketplace/${selectedItem.id}/edit`);
    }
  };

  // Navigate back from edit to detail
  const handleCancelEdit = () => {
    if (selectedItem) {
      navigate(`/admin/marketplace/${selectedItem.id}`);
    }
    cancelEditing();
  };

  // Format price for display
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {isDetailView ? (
              <button
                onClick={handleBackToList}
                className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition"
              >
                <ArrowLeft size={20} />
                <span>Back to list</span>
              </button>
            ) : (
              <Link
                to="/admin/dashboard"
                className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition"
              >
                <ArrowLeft size={20} />
                <span>Dashboard</span>
              </Link>
            )}
            <h1 className="text-xl font-bold text-neutral-900">{t('title')}</h1>
            <div className="w-24"></div> {/* Spacer for alignment */}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-full mx-auto h-[calc(100vh-64px)]">
        <div className="grid grid-cols-[280px_1fr] h-full">
          {/* Left Sidebar - Filters */}
          <div className="bg-white border-r border-neutral-200 p-6 overflow-y-auto">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">
              Filters
            </h2>
            
            {/* Search Input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder={t('search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>
            
            <MarketplaceFilters
              statusFilter={statusFilter}
              categoryFilter={categoryFilter}
              categories={categories}
              onStatusFilterChange={setStatusFilter}
              onCategoryFilterChange={setCategoryFilter}
            />
          </div>

          {/* Right Content Area */}
          <div className="bg-neutral-50 overflow-y-auto">
            {isDetailView ? (
              /* Detail/Edit View - show when URL has itemId */
              <div className="bg-white h-full p-6 overflow-y-auto">
                {isLoadingDetails || !selectedItem ? (
                  /* Loading state for detail view */
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                      <p className="text-sm text-neutral-600">{t('list.loading')}</p>
                    </div>
                  </div>
                ) : (
                  <MarketplaceDetail
                    item={selectedItem}
                    categories={categories}
                    isEditing={isEditing}
                    onDelete={deleteItem}
                    onRestore={restoreItem}
                    onExpire={expireItem}
                    onReactivate={reactivateItem}
                    onEdit={handleEdit}
                    onCancelEdit={handleCancelEdit}
                    onSaveEdit={saveItemChanges}
                  />
                )}
              </div>
            ) : (
              /* Item Grid Results */
              <div className="p-6">
                {/* Results Count */}
                <div className="mb-4 text-sm text-neutral-600">
                  {t('pagination.showing', {
                    from: totalCount > 0 ? (currentPage - 1) * 50 + 1 : 0,
                    to: Math.min(currentPage * 50, totalCount),
                    total: totalCount,
                  })}
                </div>

                {/* Item Grid */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                      <p className="text-sm text-neutral-600">{t('list.loading')}</p>
                    </div>
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-neutral-500">{t('list.empty')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleSelectItem(item.id)}
                        className="bg-white border border-neutral-200 rounded-lg p-4 hover:shadow-md transition text-left"
                      >
                        <div className="aspect-video w-full bg-neutral-100 rounded-lg mb-3 overflow-hidden">
                          {item.photoUrl ? (
                            <img
                              src={buildImageUrl(item.photoUrl, 'card')}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-neutral-400">
                              <ShoppingBag size={32} />
                            </div>
                          )}
                        </div>
                        <h3 className="font-semibold text-neutral-900 mb-1 truncate">
                          {item.title}
                        </h3>
                        <p className="text-lg font-bold text-primary-600 mb-1">
                          {formatPrice(item.price)}
                        </p>
                        <p className="text-xs text-neutral-500 mb-2 truncate">
                          {item.category}
                        </p>
                        <div className="flex items-center justify-between">
                          <MarketplaceStatusBadge status={item.status} />
                          <span className="text-xs text-neutral-400">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-4">
                    <button
                      onClick={previousPage}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                      {t('pagination.previous')}
                    </button>
                    <span className="text-sm text-neutral-600">
                      {t('pagination.page', { current: currentPage, total: totalPages })}
                    </span>
                    <button
                      onClick={nextPage}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('pagination.next')}
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
